import { _decorator, assetManager, CCInteger, IRenderData, Node, NodeEventType, SpriteAtlas, SpriteFrame, UIRenderer, Vec2, math, CurveRange } from 'cc';
import { DEV, EDITOR } from 'cc/env';

class Joint {
    x: number = 0;
    y: number = 0;
    dis: number = 0;
    cos: number = 0;
    sin: number = 0;
    time: number = 0;
    critical: boolean = false; // 關鍵轉彎節點
    turnAngle: number = 0;
    turnSpace: number = 0;
}

class JointExpand {
    x0: number = 0;
    y0: number = 0;
    x1: number = 0;
    y1: number = 0;

    constructor(x0: number, y0: number, x1: number, y1: number) {
        this.x0 = x0;
        this.y0 = y0;
        this.x1 = x1;
        this.y1 = y1;
    }
}

const { ccclass, property, menu } = _decorator;

@ccclass
@menu('SmoothMotionTrail')
export default class SmoothMotionTrail extends UIRenderer {
    @property({ type: SpriteAtlas, readonly: true })
    protected atlas: SpriteAtlas = null;

    @property
    private _spriteFrame: SpriteFrame = null;

    @property
    private _isWorldXy: boolean = true;

    @property({ displayName: DEV && '偏移' })
    private _offset: Vec2 = new Vec2();

    @property
    private _lifeTime: number = 0.5;

    @property
    private _headWidth: number = 100;

    @property
    private _widthCurve: CurveRange = new CurveRange();

    @property
    private _headOpacity: number = 255;

    @property
    private _opacityCurve: CurveRange = new CurveRange();

    @property
    private _minSeg: number = 1;

    @property
    private _flipY: boolean = false;

    @property({ displayName: '轉角角度限制(度)', min: 10, max: 180, group: '轉角處理', tooltip: DEV && '夾角超過此角度時, 視為轉角' })
    private readonly miterEulerAngleLimit: number = 30;

    @property({ displayName: '最大分離轉角距離', group: '轉角處理', min: 0, tooltip: DEV && '進入轉角後, 離轉角多遠再開始畫線' })
    private readonly splitTurnMaxDistance: number = 10;

    @property({ displayName: '轉角範圍內淡出距離', group: '轉角處理', min: 0, tooltip: DEV && '於轉角, 該距離範圍內做淡出' })
    private readonly turnFadeDistance: number = 30;

    @property({ displayName: '轉角範圍淡出曲線', group: '轉角處理', tooltip: DEV && '於轉角淡出範圍內, 淡出的曲線' })
    private readonly turnFadeCurve: CurveRange = new CurveRange();

    public isPaused: boolean = false;
    private _joints: Joint[] = [];
    private _cascadeScale: Vec2 = new Vec2(1, 1);
    private _visibleScale: Vec2 = new Vec2(1, 1);

    @property({ type: SpriteFrame })
    get spriteFrame() {
        return this._spriteFrame;
    }
    set spriteFrame(val) {
        this._spriteFrame = val;
        this._updateSpriteFrame();
        this._updateUv();
        this.markForUpdateRenderData();
    }

    @property({ displayName: DEV && '世界座標' })
    get isWorldXy() {
        return this._isWorldXy;
    }
    set isWorldXy(val) {
        this._isWorldXy = val;
        this.resetPos();
    }

    @property({ displayName: DEV && '拖尾持續時間(秒)' })
    get lifeTime() {
        return this._lifeTime;
    }
    set lifeTime(v: number) {
        this._lifeTime = Math.max(0.01, v);
    }

    @property({ displayName: DEV && '頭部寬度' })
    get headWidth() {
        return this._headWidth;
    }
    set headWidth(val) {
        this._headWidth = Math.max(val, 0);
        this._updateWidth();
    }

    @property({ displayName: DEV && '寬度曲線' })
    get widthCurve() {
        return this._widthCurve;
    }
    set widthCurve(val: CurveRange) {
        this._widthCurve = val;
        this._updateWidth();
    }

    @property({ type: CCInteger, min: 0, max: 255, slide: true, displayName: DEV && '頭部透明度' })
    get headOpacity() {
        return this._headOpacity;
    }
    set headOpacity(val) {
        this._headOpacity = val;
        this._updateTrailColor();
    }

    @property({ displayName: DEV && '透明度曲線' })
    get opacityCurve() {
        return this._opacityCurve;
    }
    set opacityCurve(val: CurveRange) {
        this._opacityCurve = val;
        this._updateTrailColor();
    }

    @property({ displayName: DEV && '反轉貼圖Y軸' })
    get flipY() {
        return this._flipY;
    }
    set flipY(val) {
        this._flipY = val;
        this._updateUv();
    }

    @property({ displayName: DEV && '最小片段距離' })
    get minSeg() {
        return this._minSeg;
    }
    set minSeg(v: number) {
        this._minSeg = Math.max(0.1, v);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public __preload(): void {
        super.__preload();
        this._assembler = {
            updateColor: this._updateTrailColor.bind(this),
            updateRenderData: this._updateRenderData.bind(this),
            fillBuffers: this._fillBuffer.bind(this),
        };
        this._useVertexOpacity = true;
        this.updateMaterial();
        this._updateSpriteFrame();
    }

    public onEnable(): void {
        super.onEnable();
        this._joints = [];
        // this.updateLength();
        this._updateUv();
        this._updateWidth();
        this._updateCascadeScale();
        this._updateVisibleScale();
        this.resetPos();
        this.node.on(NodeEventType.TRANSFORM_CHANGED, this._updateVisibleScale, this);
    }
    public onDisable(): void {
        super.onDisable();
        this.node.off(NodeEventType.TRANSFORM_CHANGED, this._updateVisibleScale, this);
    }

    public resetPos(): void {
        this._joints = [];
        const now = performance.now() / 1000;
        let tx = this._offset.x;
        let ty = this._offset.y;
        if (this._isWorldXy) {
            const m = this.node.worldMatrix;
            tx += m.m12;
            ty += m.m13;
        } else {
            tx += this.node.position.x;
            ty += this.node.position.y;
        }

        // add two joints to prevent no joints
        this._addJoint(tx, ty, now);
        this._addJoint(tx, ty, now);

        this._ensureBuffer(2);
        this._updateUv();
        this._updateTrailColor();
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _render(render: any): void {
        render.commitComp(this, this._renderData, this._spriteFrame, this._assembler, null);
        this.markForUpdateRenderData();
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    protected _canRender(): boolean {
        return super._canRender() && !!this._spriteFrame?.texture;
    }

    private _updateWidth(): void {
        if (!this._joints || this._joints.length === 0) {
            return; // 尚未建立 joints 時不更新寬度
        }

        for (let i = 1; i < this._joints.length; i++) {
            const a = this._joints[i - 1];
            const b = this._joints[i];
            if (!a || !b) {
                continue; // 防止 undefined
            }
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            b.dis = Math.sqrt(dx * dx + dy * dy);
        }
    }

    private _ensureBuffer(nodeCount: number): void {
        const wantVerts = Math.max(2, nodeCount) << 1;
        if (!this._renderData) {
            // 建立新的 renderData
            this.destroyRenderData();
            const rd = this.requestRenderData();
            rd.dataLength = wantVerts;
            rd.resize(wantVerts, (wantVerts - 2) * 3);
            this._updateIData();
            this._updateUv();
            this._updateTrailColor();
            return;
        }
        // 若現有大小不符，resize（避免每次都 destroy）
        if (this._renderData.dataLength !== wantVerts) {
            this._renderData.dataLength = wantVerts;
            this._renderData.resize(wantVerts, (wantVerts - 2) * 3);
            this._updateIData();
            this._updateUv();
            this._updateTrailColor();
        }
    }

    private _updateIData(): void {
        if (!this._renderData) {
            return;
        }
        // ib is readonly, use ['_ib'] force update
        // eslint-disable-next-line dot-notation
        this._renderData.chunk['_ib'] = new Uint16Array(this._renderData.chunk.indexCount);
        // eslint-disable-next-line dot-notation
        const ib = this._renderData.chunk['_ib'];
        for (let i = 0, id = 0, len = ib.length; i < len; ++id) {
            ib[i++] = id;
            ib[i++] = id + 1;
            ib[i++] = id + 2;
        }
    }
    private _updateRenderData(): void {
        if (!this._spriteFrame || this.isPaused) {
            return;
        }

        if (!this._joints || this._joints.length === 0) {
            this.resetPos();
        }

        this._updateXy();
        this._updateTrailColor();
        this._updateUv();

        if (this._renderData && this._spriteFrame) {
            this._renderData.updateRenderData(this, this._spriteFrame);
        }
    }

    private _updateCascadeScale(): void {
        let n = this.node;
        let sx = 1;
        let sy = 1;
        while (n.parent !== null) {
            n = n.parent;
            sx *= n.scale.x;
            sy *= n.scale.y;
        }
        this._cascadeScale.x = sx;
        this._cascadeScale.y = sy;
    }
    private _updateVisibleScale(type: number = 4): void {
        if (type & Node.TransformBit.SCALE) {
            this._visibleScale.x = this._cascadeScale.x * this.node.scale.x;
            this._visibleScale.y = this._cascadeScale.y * this.node.scale.y;
        }
    }

    private _updateXy(): void {
        if (!this._spriteFrame || this.isPaused) {
            return;
        }
        if (!this._joints) {
            this._joints = [];
        }

        const now = performance.now() / 1000;
        const vsx = this._visibleScale.x;
        const vsy = this._visibleScale.y;

        // get current pos
        let curX = 0;
        let curY = 0;
        if (this._isWorldXy) {
            const m = this.node.worldMatrix;
            curX = this._offset.x * vsx + m.m12;
            curY = this._offset.y * vsy + m.m13;
        } else {
            curX = this._offset.x * this.node.scale.x + this.node.position.x;
            curY = this._offset.y * this.node.scale.y + this.node.position.y;
        }

        // insert or update head
        if (this._joints.length === 0) {
            // prevent no joints
            this._addJoint(curX, curY, now);
            this._addJoint(curX, curY, now);
        } else {
            const head = this._joints[0];
            const dx = curX - head.x;
            const dy = curY - head.y;
            const distSq = dx * dx + dy * dy;

            if (this._joints.length > 1) {
                const next = this._joints[1];
                const ndx = head.x - next.x;
                const ndy = head.y - next.y;

                // make turn add critical turn joint
                if (!head.critical && this._detectTurn(dx, dy, ndx, ndy) && distSq >= this._minSeg * this._minSeg) {
                    head.critical = true;

                    const angle = this._calculateAngle(dx, dy, ndx, ndy);
                    const restrict = math.toRadian(this.miterEulerAngleLimit);
                    const ratio = (angle - restrict) / (Math.PI - restrict);
                    head.turnSpace = this.splitTurnMaxDistance * ratio;
                    head.turnAngle = angle;

                    // copy one more at head to disjoint
                    const nj = this._addJoint(head.x, head.y, head.time);
                    nj.critical = true;
                    nj.turnSpace = head.turnSpace;
                    nj.turnAngle = head.turnAngle;
                    return;
                }
            }

            if (head.critical) {
                if (distSq >= head.turnSpace * head.turnSpace) {
                    // turn case: add new joint, after turnSpace reached
                    this._addJoint(curX, curY, now);
                }
            } else if (distSq >= this._minSeg * this._minSeg && !head.critical) {
                // normal case: add new joint
                this._addJoint(curX, curY, now);
            } else if (!head.critical) {
                // update head time/pos to keep it fresh so alpha calculation works
                head.time = now;
                head.x = curX;
                head.y = curY;
            }
        }

        // remove any dead joints by age BEFORE building buffer (avoid thin remnants)
        for (let i = this._joints.length - 1; i >= 0; --i) {
            const age = now - this._joints[i].time;
            if (age >= this._lifeTime) {
                this._joints.splice(i, 1);
            }
        }
        // 保證至少 2 個
        if (this._joints.length < 2) {
            this._addJoint(curX, curY, now);
            this._addJoint(curX, curY, now);
        }

        // ensure buffer
        const nodeCount = Math.max(2, this._joints.length);
        this._ensureBuffer(nodeCount);

        // compute directions and fill vertices
        const { data } = this._renderData;
        let tx = 0;
        let ty = 0;
        let csx = 1;
        let csy = 1;
        if (!this._isWorldXy) {
            tx = this.node.position.x;
            ty = this.node.position.y;
            csx = this._cascadeScale.x;
            csy = this._cascadeScale.y;
        }

        const len = this._joints.length;
        for (let i = 0; i < len - 1; ++i) {
            const cur = this._joints[i];
            const prev = this._joints[i - 1];
            const next = this._joints[i + 1];
            const ax = cur.x - next.x;
            const ay = cur.y - next.y;
            const L = Math.sqrt(ax * ax + ay * ay);
            if (cur.critical) {
                if (prev && !prev.critical) {
                    cur.cos = this._joints[i - 1].cos;
                    cur.sin = this._joints[i - 1].sin;
                } else if (next && !next.critical) {
                    cur.cos = this._joints[i + 1].cos;
                    cur.sin = this._joints[i + 1].sin;
                }
            } else if (L > 1e-6) {
                cur.cos = ax / L;
                cur.sin = ay / L;
            } else {
                cur.cos = 1;
                cur.sin = 0;
            }
        }
        // tail direction copy previous
        const lastIdx = len - 1;
        if (lastIdx >= 0) {
            const last = this._joints[lastIdx];
            if (len >= 2) {
                last.cos = this._joints[lastIdx - 1].cos;
                last.sin = this._joints[lastIdx - 1].sin;
            } else {
                last.cos = 1;
                last.sin = 0;
            }
        }
        // fill vertices: width driven by AGE (not by index) to guarantee tail shrinks
        let vid = 0;
        for (let i = 0; i < nodeCount; ++i) {
            const j = this._joints[i] || this._joints[this._joints.length - 1];
            if (!j) {
                continue;
            }
            const lineExpand = this._lineExpand(j, now, tx, ty, csx, csy, vsx, vsy);

            if (j.critical && this._joints[i + 1] && !this._joints[i + 1].critical) {
                // 靠近尾的轉折, 由於是已經畫出的結果, 不做過多的改變
                const r = 0.5;
                const lineExpand2 = this._lineExpand(this._joints[i + 1], now, tx, ty, csx, csy, vsx, vsy);
                data[vid].x = math.lerp(lineExpand.x0, lineExpand2.x0, r);
                data[vid].y = math.lerp(lineExpand.y0, lineExpand2.y0, r);
                data[vid + 1].x = math.lerp(lineExpand.x1, lineExpand2.x1, r);
                data[vid + 1].y = math.lerp(lineExpand.y1, lineExpand2.y1, r);
                vid += 2;
            } else if (j.critical && this._joints[i - 1] && !this._joints[i - 1].critical) {
                // 靠近頭的轉折
                let r = 0.5;
                const lineExpand2 = this._lineExpand(this._joints[i - 1], now, tx, ty, csx, csy, vsx, vsy);
                const dsquare = (this._joints[i - 1].x - j.x) * (this._joints[i - 1].x - j.x) + (this._joints[i - 1].y - j.y) * (this._joints[i - 1].y - j.y);
                if (dsquare > j.turnSpace * j.turnSpace) {
                    r = j.turnSpace / Math.sqrt(dsquare);
                }
                r = math.clamp01(r);
                data[vid].x = math.lerp(lineExpand.x0, lineExpand2.x0, r);
                data[vid].y = math.lerp(lineExpand.y0, lineExpand2.y0, r);
                data[vid + 1].x = math.lerp(lineExpand.x1, lineExpand2.x1, r);
                data[vid + 1].y = math.lerp(lineExpand.y1, lineExpand2.y1, r);
                vid += 2;
            } else {
                data[vid].x = lineExpand.x0;
                data[vid].y = lineExpand.y0;
                data[vid + 1].x = lineExpand.x1;
                data[vid + 1].y = lineExpand.y1;
                vid += 2;
            }
        }

        // commit to vb (world/local transform)
        this._fitXy(data);
    }

    private _fitXy(data: IRenderData[]): void {
        const { vb } = this._renderData.chunk;
        if (this._isWorldXy) {
            for (let i = 0, len = vb.length, step = this._renderData.floatStride, id = 0; i < len; i += step, ++id) {
                vb[i] = data[id].x;
                vb[i + 1] = data[id].y;
            }
        } else {
            const m = this.node.worldMatrix;
            const { m12, m13 } = m;
            for (let i = 0, len = vb.length, step = this._renderData.floatStride, id = 0; i < len; i += step, ++id) {
                vb[i] = data[id].x + m12;
                vb[i + 1] = data[id].y + m13;
            }
        }
    }

    private _updateUv(): void {
        const spriteFrame = this._spriteFrame;
        if (!spriteFrame || !this._renderData) {
            return;
        }

        const nodeCount = Math.max(2, this._joints.length);
        const renderData = this._renderData;
        const { vb } = renderData.chunk;
        const step = renderData.floatStride;
        // 防止除以 0
        const uvStep = 1 / Math.max(1, nodeCount - 1);

        for (let i = 3, id = 0, len = vb.length; i < len; i += step, ++id) {
            vb[i] = id & 1;
            const v = 1 - uvStep * (id >> 1);
            vb[i + 1] = this._flipY ? 1 - v : v;
        }

        const { uv } = spriteFrame;
        if (spriteFrame.rotated) {
            const uvL = uv[0];
            const uvB = uv[1];
            const uvW = uv[4] - uvL;
            const uvH = uv[3] - uvB;
            for (let i = 3, len = vb.length, s = renderData.floatStride; i < len; i += s) {
                const tmp = vb[i];
                vb[i] = uvL + vb[i + 1] * uvW;
                vb[i + 1] = uvB + tmp * uvH;
            }
        } else {
            const uvL = uv[0];
            const uvB = uv[1];
            const uvW = uv[2] - uvL;
            const uvH = uv[5] - uvB;
            for (let i = 3, len = vb.length, s = renderData.floatStride; i < len; i += s) {
                vb[i] = uvL + vb[i] * uvW;
                vb[i + 1] = uvB + vb[i + 1] * uvH;
            }
        }
    }
    private _updateTrailColor(): void {
        if (!this._renderData) {
            return;
        }
        const { vb } = this._renderData.chunk;
        if (!vb || vb.length === 0) {
            return;
        }

        const r = this.color.r / 255;
        const g = this.color.g / 255;
        const b = this.color.b / 255;
        const uiPropOpacity = this.node._uiProps.opacity;
        const a = (this.color.a * uiPropOpacity) / 255;

        const now = performance.now() / 1000;

        // remove dead joints before coloring (safety)
        for (let i = this._joints.length - 1; i >= 0; --i) {
            const age = now - this._joints[i].time;
            if (age >= this._lifeTime) {
                this._joints.splice(i, 1);
            }
        }
        if (this._joints.length < 2) {
            return;
        }

        const nodeCount = Math.max(2, this._joints.length);
        const stride = this._renderData.floatStride * 2;
        const turns = this._joints.filter((j) => j.critical);

        for (let i = 0; i < nodeCount; ++i) {
            const j = this._joints[i] || this._joints[this._joints.length - 1];
            const age = Math.max(0, Math.min(1, (now - j.time) / this._lifeTime));
            const opa = (this.headOpacity / 255) * this.opacityCurve.evaluate(1 - age, 0);
            const clamped = Math.max(0, Math.min(1, opa));
            const baseIndex = i * stride + 5;

            // fade out near turns
            const disjoint = j.critical;
            let turnFade = 1.0;
            turns.forEach((turn) => {
                const dist = (j.x - turn.x) * (j.x - turn.x) + (j.y - turn.y) * (j.y - turn.y);
                if (dist < this.turnFadeDistance * this.turnFadeDistance) {
                    const d = Math.sqrt(dist);
                    const f = d / this.turnFadeDistance;
                    turnFade *= this.turnFadeCurve.evaluate(f, 0);
                }
            });

            vb[baseIndex] = r;
            vb[baseIndex + 9] = r;
            vb[baseIndex + 1] = g;
            vb[baseIndex + 10] = g;
            vb[baseIndex + 2] = b;
            vb[baseIndex + 11] = b;

            const alpha = disjoint ? 0 : a * clamped;
            const finalAlpha = alpha * turnFade;
            vb[baseIndex + 3] = finalAlpha;
            vb[baseIndex + 12] = finalAlpha;
        }
    }
    private _updateSpriteFrame(): void {
        const spriteFrame = this._spriteFrame;
        if (!spriteFrame) {
            this.atlas = null;
            return;
        }
        if (this._renderData) {
            this._renderData.textureDirty = true;
        }
        if (EDITOR) {
            if (!spriteFrame.atlasUuid) {
                this.atlas = null;
                return;
            }
            assetManager.loadAny(spriteFrame.atlasUuid, (err: Error, asset: SpriteAtlas) => {
                if (err) {
                    this.atlas = null;
                    return;
                }
                this.atlas = asset;
            });
        }
    }

    private _fillBuffer(): void {
        const renderData = this._renderData;
        const { chunk } = renderData;
        const vid = chunk.vertexOffset;
        const { meshBuffer } = chunk;
        const { iData } = meshBuffer;
        // eslint-disable-next-line dot-notation
        const ib = chunk['_ib'];
        for (let i = 0, len = renderData.indexCount, offset = meshBuffer.indexOffset; i < len; iData[offset++] = vid + ib[i++]);
        meshBuffer.indexOffset += renderData.indexCount;
    }

    private _addJoint(x: number, y: number, time: number): Joint {
        const nj = new Joint();
        nj.x = x;
        nj.y = y;
        nj.time = time;
        this._joints.unshift(nj);
        return nj;
    }

    /**
     * @return 逕度 0 ~ PI
     *  */
    private _calculateAngle(dx0, dy0, dx1, dy1): number {
        const angle1 = Math.atan2(dy0, dx0);
        const angle2 = Math.atan2(dy1, dx1);
        const angleDiff = Math.abs(angle2 - angle1);
        if (angleDiff > Math.PI) {
            return Math.PI * 2 - angleDiff;
        }
        return angleDiff;
    }

    private _detectTurn(dx0, dy0, dx1, dy1): boolean {
        return this._calculateAngle(dx0, dy0, dx1, dy1) > math.toRadian(this.miterEulerAngleLimit);
    }

    private _lineExpand(j: Joint, now: number, tx: number, ty: number, csx: number, csy: number, vsx: number, vsy: number): JointExpand {
        const age = Math.max(0, Math.min(1, (now - j.time) / this._lifeTime));
        const width = this._headWidth * this._widthCurve.evaluate(1 - age, 0);
        const half = width * 0.5;

        const disX = half * vsx * j.sin;
        const disY = half * vsy * j.cos;
        const px = (j.x - tx) * csx;
        const py = (j.y - ty) * csy;

        const p0x = px + disX;
        const p0y = py - disY;
        const p1x = px - disX;
        const p1y = py + disY;
        return new JointExpand(p0x, p0y, p1x, p1y);
    }
}
